import DetailsTitle from "components/common/detailsPage/DetailsTitle";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { formatUTCDate } from "utils/utils";
import InfoItem from "components/common/detailsPage/InfoItem";
import { Copy, Download } from "lucide-react";
import { showAlert } from "store/slices/alertSlice";
import HorizontalScroller from "components/common/HorizontalScroller";
import TruncatedText from "components/common/TruncatedText";
import { getAuditLogDetails } from "store/slices/loggersSlice";

const AuditLogDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const id = useQueryParam("id");

  const { canAction } = usePermission();
  const { canView } = canAction("audit_logs");

  const { loading, auditLogDetails: itemDetails } = useSelector(
    (state) => state.CommonLogs,
  );

  const { logInfo, performedBy, changes, snapshot } = itemDetails || {};

  useEffect(() => {
    if (id && canView) {
      dispatch(getAuditLogDetails(id));
    }
  }, [id, canView]);

  const formattedData = (data) => data?.replace(/\n/g, "<br>");

  const copyToClipboard = (data, type) => {
    const dataString = JSON.stringify(data, null, 2);

    const textarea = document.createElement("textarea");
    textarea.value = dataString;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    if (dataString === "null") {
      return dispatch(showAlert({ text: t("Nothing to copy"), type: "error" }));
    }

    try {
      textarea.select();
      const successful = document.execCommand("copy");
      if (successful) {
        dispatch(
          showAlert({
            text: t(`${type} copied to clipboard!`),
            type: "success",
          }),
        );
      } else {
        dispatch(showAlert({ text: t("Failed to copy"), type: "error" }));
      }
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "error" }));
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const downloadJSON = (data) => {
    const dataString = JSON.stringify(data, null, 2);
    if (dataString === "null") {
      return dispatch(
        showAlert({ text: t("Nothing to download"), type: "error" }),
      );
    }
    const blob = new Blob([dataString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "data.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const auditLogInfoFields = [
    // ───── Customer Info ─────
    {
      label: t("Log ID"),
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{logInfo?.logId || "-"}</div>
        </HorizontalScroller>
      ),
      col: { lg: 6 },
    },
    {
      label: t("Module"),
      value: logInfo?.module || "-",
      col: { lg: 6 },
    },
    {
      label: t("Action"),
      value: (
        <div>
          {logInfo?.action === "DELETE" ? (
            <span className="membership_log_badge badge-error">
              {logInfo?.action || "-"}
            </span>
          ) : logInfo?.action === "UPDATE" ? (
            <span className="membership_log_badge badge-info">
              {logInfo?.action || "-"}
            </span>
          ) : (
            <span className="membership_log_badge">
              {logInfo?.action || "-"}
            </span>
          )}
        </div>
      ),
      col: { lg: 6 },
    },
    {
      label: t("Date & Time: "),
      value: formatUTCDate(logInfo?.dateTime),
      col: { lg: 6 },
    },
    {
      label: t("Summery"),
      value: <TruncatedText text={logInfo?.summary || ""} />,
      col: { lg: 12 },
    },
  ];
  const performedByFields = [
    {
      label: t("Id"),
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{performedBy?._id || "-"}</div>
        </HorizontalScroller>
      ),
      col: { lg: 6 },
    },
    {
      label: t("Name"),
      value: performedBy?.name || "-",
      col: { lg: 6 },
    },
    { isSpacer: true },
    {
      label: t("Role"),
      value: performedBy?.role || "-",
      col: { lg: 6 },
    },
    {
      label: t("Model"),
      value: performedBy?.model || "-",
      col: { lg: 6 },
    },
  ];

  const shimmerLayout = [
    {
      col: 4,
      blocks: [
        { rows: 2, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
    {
      col: 4,
      blocks: [
        { rows: 1, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
    {
      col: 4,
      blocks: [
        { rows: 2, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
    {
      col: 12,
      blocks: [
        { rows: 3, cols: 4 },
        { rows: 1, cols: 2 },
      ],
    },
    {
      col: 12,
      type: "details_page_tab",
      blocks: [{ rows: 1, cols: 2 }],
    },
  ];

  if (!canView) return null;

  if (loading || !itemDetails) {
    // if (!loading) {
    return (
      <div className="page-content">
        <DetailsTitle title={t("Audit Log Details")} />
        <DetailsShimmer layout={shimmerLayout} />
      </div>
    );
  }

  return (
    <div className="page-content common_details_page membership_log_details">
      <DetailsTitle title={t("Audit Log Details")} />

      <Row>
        <Col lg={4} className="">
          <div className="common_card" style={{ height: "calc(100%  - 20px)" }}>
            <Row id="general_detail_row">
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Log Information")}</h4>
              </Col>

              {auditLogInfoFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
        <Col lg={4} className="">
          <div className="common_card" style={{ height: "calc(100%  - 20px)" }}>
            <Row id="device_detail_row">
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Performed By")}</h4>
              </Col>
              {performedByFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
        <Col lg={4} className="">
          <div className="common_card" style={{ height: "calc(100%  - 20px)" }}>
            <Row>
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Changes")}</h4>
              </Col>
              <div className="bg-black text-white p-3 rounded-4 position-relative state_change_viewer audit_log_changes">
                <pre
                  dangerouslySetInnerHTML={{
                    __html: formattedData(JSON.stringify(changes, null, 2)),
                  }}
                />
                <Copy
                  style={{
                    position: "absolute",
                    top: 20,
                    right: 55,
                    cursor: "pointer",
                  }}
                  onClick={() => copyToClipboard(changes, "Changes")}
                />
                <Download
                  style={{
                    position: "absolute",
                    top: 20,
                    right: 20,
                    cursor: "pointer",
                  }}
                  onClick={() => downloadJSON(changes)}
                />
              </div>
            </Row>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <div className="details_page_title">
            <h2 className="mt-4">{t("Detailed State Change")}</h2>
          </div>
          <Row>
            <Col lg={6}>
              <div className="state_changes_wrapper common_card">
                <span className="state_change_title previous">
                  {t("Previous State")}
                </span>

                <div className="bg-black text-white p-3 rounded-4 position-relative state_change_viewer">
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: formattedData(
                        JSON.stringify(snapshot?.previousState, null, 2),
                      ),
                    }}
                  />
                  <Copy
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 55,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      copyToClipboard(snapshot?.previousState, "Previous state")
                    }
                  />
                  <Download
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      cursor: "pointer",
                    }}
                    onClick={() => downloadJSON(snapshot?.previousState)}
                  />
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="state_changes_wrapper common_card">
                <span className="state_change_title new">{t("New State")}</span>

                <div className="bg-black text-white p-3 rounded-4 position-relative state_change_viewer">
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: formattedData(
                        JSON.stringify(snapshot?.newState, null, 2),
                      ),
                    }}
                  />
                  <Copy
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 55,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      copyToClipboard(snapshot?.newState, "New state")
                    }
                  />
                  <Download
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      cursor: "pointer",
                    }}
                    onClick={() => downloadJSON(snapshot?.newState)}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default AuditLogDetails;
